import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import Box from '@ui/Box'
import TextArea from '@ui/TextArea'
import CopyToClipboardButton from '@ui/CopyToClipboardButton'
import routes from '@routes'
import { BuilderContext } from '../../app/builder/[id]/utils'
import styled from 'styled-components'
import HelpButton from '@components/HelpButton/HelpButton'
import { Collapse, Drawer, Skeleton, Space } from 'antd'
import { Color } from '@interfaces/designSystem'
import { formatDateTime } from '@utils/format'
import IconButton from '@ui/IconButton'
import Flex from '@ui/Flex'
import {
  checkPublishStatus,
  publishPreviewWidget,
  publishWidget,
  resetPublishStatus,
  saveWidget,
  useCheckIsInABTest,
  useFetchWidget,
} from '@api/widgets'
import { withHandledErrors } from '@utils/errors'
import { useNotification } from '@ui/NotificationProvider'
import Grid from '@ui/Grid'
import { BiLinkExternal } from 'react-icons/bi'
import { AnalyticsEvents, sendAnalyticsEvent } from '@utils/analytics'
import ElementsSelector from '@components/ShareCode/ElementsSelector'
import { ElementsType } from '@enhanci/enhanci-lib'

type Props = {
  afterPublish?: boolean
}

const Video = styled.video`
  width: 100%;
`

const PlatformBtn = styled.div`
  width: 180px;
  height: 50px;
  border: 1px solid ${Color.Grey_10};
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  text-align: center;
`

const PlatformImg = styled.img<{ $width?: string }>`
  width: ${({ $width }) => $width || 'auto'};
  height: 100%;
  margin: auto;
`

const Platforms = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, 180px);
  justify-content: space-between;
  grid-gap: 16px;
`

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`

const Platform = ({
  bg,
  onClick,
  width,
}: {
  bg: string
  onClick: () => void
  width?: string
}) => {
  return (
    <PlatformBtn onClick={onClick}>
      <PlatformImg src={bg} $width={width} />
    </PlatformBtn>
  )
}

const ShareCodeContent = ({ afterPublish }: Props) => {
  const [sideDrawer, setSideDrawer] = React.useState<
    { title: string; url: string } | undefined
  >()
  const { widget } = useContext(BuilderContext)
  const { data: widgetData, refetch: refetchWidget } = useFetchWidget(widget.id!)
  const { data: isInABTest } = useCheckIsInABTest(widget.id!)
  const [loading, setLoading] = React.useState(false)
  const notification = useNotification()
  const timerId = useRef<NodeJS.Timer>()
  const [iframeLoading, setIframeLoading] = React.useState(false)
  const [elements, setElements] = useState<ElementsType[]>(['pricing', 'compare', 'faq'])

  const fetchStatus = useCallback(async () => {
    clearTimeout(timerId.current)
    const resp = await checkPublishStatus(widget.id!, false)

    if (resp.status === 'success') {
      setLoading(false)
      await resetPublishStatus(widget.id!)
      refetchWidget()
    }

    if (resp.status === 'queuing' || resp.status === 'publishing') {
      setLoading(true)
      timerId.current = setTimeout(fetchStatus, 3000)
    }

    if (resp.status === 'failed') {
      setLoading(false)
      notification.showError('Widget publishing failed. Please try again')
      resetPublishStatus(widget.id!)
    }
  }, [widget.id])

  useEffect(() => {
    return () => {
      clearTimeout(timerId.current)
    }
  }, [])

  useEffect(() => {
    if (sideDrawer) {
      setIframeLoading(true)
    }
  }, [sideDrawer])

  const script = `<script defer src="${routes.external.widgetScript.replace(
    ':id',
    widget!.id!,
  )}"></script>
<div id="${process.env.NEXT_PUBLIC_MOUNT_TARGET_ID}"${
    !elements.length || elements.length === 3
      ? ''
      : ` data-elements="${elements.join(',')}"`
  }></div>`

  const onPublish = async () => {
    await withHandledErrors(
      notification,
      async () => {
        await saveWidget(widget)
        await publishWidget(widget.id!)
        // do now await here, because we don't want to wait for the response
        publishPreviewWidget(widget.id!)
        fetchStatus()
      },
      null,
    )
  }

  return (
    <Box>
      <Box fontSize={16} mb={8}>
        <Box>Copy and paste this code where you want to show the widget</Box>
        {!afterPublish && isInABTest === false && (
          <Flex fontSize={14} color={Color.Grey_80}>
            {widgetData?.lastPublishedAt ? (
              <>
                Last published on {formatDateTime(new Date(widgetData.lastPublishedAt))}
              </>
            ) : (
              <>Hasn't been published yet</>
            )}
            <Box ml={8}>
              <IconButton
                size="small"
                type="default"
                loading={loading}
                onClick={onPublish}
              >
                {loading ? 'Queueing...' : 'Publish'}
              </IconButton>
            </Box>
          </Flex>
        )}
      </Box>
      <TextArea rows={4} value={script} />
      <CopyToClipboardButton text={script} />
      <ElementsSelector value={elements} onChange={setElements} />
      <br />
      <Collapse
        items={[
          {
            key: '1',
            label: 'I need some help with installation',
            children: (
              <Box>
                <Box>
                  <HelpButton label="I need some help with installation" small />
                </Box>
              </Box>
            ),
          },
        ]}
      />
      <Box mt={24}>
        <Box fontSize={20}>Supported platforms:</Box>
        <Box mb={16}>Click on the platform to see the installation instruction</Box>
        <Platforms>
          <Platform
            bg="/img/platforms/wix.svg"
            onClick={() => {
              sendAnalyticsEvent(AnalyticsEvents.wixInstructionClicked)
              setSideDrawer({
                title: 'How to add Pricing Table to Wix',
                url: routes.publicRoutes.wixInstruction,
              })
            }}
          />
          <Platform
            bg="/img/platforms/squarespace.svg"
            onClick={() => {
              sendAnalyticsEvent(AnalyticsEvents.squarespaceInstructionClicked)
              setSideDrawer({
                title: 'How to add Pricing Table to Squarespace',
                url: routes.publicRoutes.squarespaceInstruction,
              })
            }}
          />
          <Platform
            bg="/img/platforms/webflow.svg"
            width="100%"
            onClick={() => {
              sendAnalyticsEvent(AnalyticsEvents.webflowInstructionClicked)
              setSideDrawer({
                title: 'How to add Pricing Table to Webflow',
                url: routes.publicRoutes.webflowInstruction,
              })
            }}
          />
          <Platform
            bg="/img/platforms/shopify.svg"
            onClick={() => {
              sendAnalyticsEvent(AnalyticsEvents.shopifyInstructionClicked)
              setSideDrawer({
                title: 'How to add Pricing Table to Shopify',
                url: routes.publicRoutes.shopifyInstruction,
              })
            }}
          />
          <Platform
            bg="/img/platforms/elementor.svg"
            onClick={() => {
              sendAnalyticsEvent(AnalyticsEvents.elementorInstructionClicked)
              setSideDrawer({
                title: 'How to add Pricing Table to Elementor',
                url: routes.publicRoutes.elementorInstruction,
              })
            }}
          />
          <Platform
            bg="/img/platforms/weebly.svg"
            onClick={() => {
              sendAnalyticsEvent(AnalyticsEvents.weeblyInstructionClicked)
              setSideDrawer({
                title: 'How to add Pricing Table to Weebly',
                url: routes.publicRoutes.weeblyInstruction,
              })
            }}
          />
          <Platform
            bg="/img/platforms/google-sites.jpeg"
            onClick={() => {
              sendAnalyticsEvent(AnalyticsEvents.googleSitesInstructionClicked)
              setSideDrawer({
                title: 'How to add Pricing Table to Google Sites',
                url: routes.publicRoutes.googleSitesInstruction,
              })
            }}
          />
          <Platform
            bg="/img/platforms/html.png"
            onClick={() => {
              sendAnalyticsEvent(AnalyticsEvents.htmlInstructionClicked)
              setSideDrawer({
                title: 'How to add Pricing Table using regular HTML',
                url: routes.publicRoutes.htmlInstruction,
              })
            }}
          />
        </Platforms>
      </Box>
      <Drawer
        title={sideDrawer?.title}
        placement="right"
        size="large"
        onClose={() => setSideDrawer(undefined)}
        open={!!sideDrawer}
      >
        {iframeLoading ? (
          <Grid flow="row" gap={16}>
            <Skeleton.Input active size="large" block />
            <Skeleton.Input active size="large" block />
            <Skeleton.Input active size="large" block />
            <Skeleton.Image active style={{ width: '100%', height: '400px' }} />
            <Skeleton.Input active size="large" block />
            <Skeleton.Input active size="large" block />
            <Skeleton.Image active style={{ width: '100%', height: '400px' }} />
          </Grid>
        ) : (
          <IconButton icon={BiLinkExternal} href={sideDrawer?.url} target="_blank">
            Open the instruction in a new tab
          </IconButton>
        )}
        <Iframe
          frameBorder={0}
          src={sideDrawer?.url}
          onLoad={() => setIframeLoading(false)}
          style={{ opacity: iframeLoading ? 0 : 1 }}
        />
      </Drawer>
    </Box>
  )
}

export default ShareCodeContent
